import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import AppWideMetrics from '@gumtree/web-bff/src/metrics/modules/app-wide-metrics';
import { getHasUserMadeChoice } from './util';
import { setConsentStatusParams, waitForConsentUpdate } from './track-consent-state';

const trackingEventsOnOneTrust = async (window) => {
    await getHasUserMadeChoice(window); // wait for banner load

    // Intercept gumtreeDataLayer.push to filter out excessive events that were triggered by `OneTrustGroupsUpdated` via GTM
    const originalPush = window.gumtreeDataLayer.push.bind(window.gumtreeDataLayer);

    window.gumtreeDataLayer.push = function (...args: any[]) {
        const event = args[0];

        /**
         * Generic helper to validate and clean internal markers for GTM event filtering
         * @param eventName - The GA4 event name to check
         * @param markerKey - The internal marker property name
         * @param fallbackCheck - Optional validation to run if marker is missing
         * @returns true if event should be allowed, false if it should be blocked
         */
        const isLegitimateEvent = (
            eventName: string,
            markerKey: string,
            fallbackCheck?: () => boolean
        ): boolean => {
            if (event?.event !== eventName) return true;

            const isFiredExplicitly = event[markerKey] === true;

            if (isFiredExplicitly) {
                delete event[markerKey];
                return true;
            }

            if (fallbackCheck) {
                return fallbackCheck();
            }

            return false;
        };

        // Block consent_banner events without explicit marker to prevent GTM-triggered duplicates
        if (!isLegitimateEvent('consent_banner', '_explicitConsentBannerEvent')) {
            return window.gumtreeDataLayer.length;
        }

        if (!isLegitimateEvent('update_privacy_preferences', '_explicitPrivacyPrefEvent')) {
            return window.gumtreeDataLayer.length;
        }

        return originalPush(...args);
    };

    const preferenceOptionsLookup: Record<string, GA4.PrivacyPrefKey> = {
        'Functional Cookies': 'functionalCookie',
        'Performance Cookies': 'performanceCookie',
        'Targeting Cookies': 'targetingCookie',
        'Google Advertising': 'googleAdvertising',
        'Adobe Marketplace': 'adobeMarketplace',
        'Microsoft Clarity': 'microsoftClarity',
        TikTok: 'tiktok',
        'TV Squared': 'tvSquared',
        'Google ATPs': 'googleAtps',
        Qualaroo: 'qualaroo',
        'Facebook Advertising': 'facebookAdvertising',
        LiveRamp: 'liveramp',
        'Store and/or access information on a device': 'storeAccessDeviceData',
        'Personalised advertising and content, advertising and content measurement, audience research and services development':
            'personalisationData',
        'Use precise geolocation data': 'geolocationData',
        'Actively scan device characteristics for identification': 'deviceIdentificationData',
    };

    const initiatePreferenceOptions = () => {
        const preferenceOptions = {} as Partial<Record<GA4.PrivacyPrefKey, boolean>>;
        const toggles = window.document.querySelectorAll('.ot-tgl input[type="checkbox"]');

        toggles.forEach((toggle) => {
            const label = toggle.closest('.ot-tgl')?.querySelector('.ot-label-txt');
            const labelValue = label?.textContent.trim();

            if (labelValue && preferenceOptionsLookup[labelValue]) {
                preferenceOptions[preferenceOptionsLookup[labelValue]] = toggle.checked;
            }
        });

        return preferenceOptions;
    };

    const firePrivacyPrefEvent = (privacyPref: any) => {
        trackGA4Event<GA4.UpdatePrivacyPreferencesEvent>({
            event: 'update_privacy_preferences',
            privacyPref,
            consentStatus: setConsentStatusParams(),
            _explicitPrivacyPrefEvent: true,
        } as any);
    };

    window.document.body.addEventListener('click', async (event) => {
        const oneTrustUI = window.document.querySelector('#onetrust-consent-sdk');
        const oneTrustBanner = window.document.querySelector('#onetrust-banner-sdk') as HTMLElement;
        const isBannerClosed =
            !oneTrustBanner || window.getComputedStyle(oneTrustBanner).display === 'none';
        const isClickInsideOneTrustUI = oneTrustUI && oneTrustUI.contains(event?.target);
        const defaultPreferenceOptions = initiatePreferenceOptions();

        if (isClickInsideOneTrustUI) {
            const bannerAcceptBtn = document.querySelector('#onetrust-accept-btn-handler');
            const bannerRejectBtn = document.querySelector('#onetrust-reject-all-handler');
            const bannerManageBtn = document.querySelector('#onetrust-pc-btn-handler');
            const preferenceAcceptBtn = document.querySelector('#accept-recommended-btn-handler');
            const preferenceRejectBtn = document.querySelector('.ot-pc-refuse-all-handler');
            const preferenceSaveBtn = document.querySelector('.save-preference-btn-handler');
            const toggledNob = event.target.closest('.ot-switch-nob');

            // Check if click is inside the banner (not preference centre)
            const isClickInsideBanner = oneTrustBanner && oneTrustBanner.contains(event?.target);

            if (
                !isBannerClosed &&
                isClickInsideBanner &&
                bannerAcceptBtn?.contains(event?.target)
            ) {
                waitForConsentUpdate().then(() => {
                    trackGA4Event<GA4.ConsentBannerEvent>({
                        event: 'consent_banner',
                        clickConsentBanner: 'accept',
                        consentStatus: setConsentStatusParams(),
                        _explicitConsentBannerEvent: true,
                    } as any);
                });

                AppWideMetrics.registerConsentBannerInteractionEvent('accept');
            }

            if (
                !isBannerClosed &&
                isClickInsideBanner &&
                bannerRejectBtn?.contains(event?.target)
            ) {
                waitForConsentUpdate().then(() => {
                    trackGA4Event<GA4.ConsentBannerEvent>({
                        event: 'consent_banner',
                        clickConsentBanner: 'reject',
                        consentStatus: setConsentStatusParams(),
                        _explicitConsentBannerEvent: true,
                    } as any);
                });

                AppWideMetrics.registerConsentBannerInteractionEvent('reject');
            }

            if (
                !isBannerClosed &&
                isClickInsideBanner &&
                bannerManageBtn?.contains(event?.target)
            ) {
                // For "manage options", capture current state immediately (no consent change yet)
                trackGA4Event<GA4.ConsentBannerEvent>({
                    event: 'consent_banner',
                    clickConsentBanner: 'manage options',
                    consentStatus: setConsentStatusParams(),
                    _explicitConsentBannerEvent: true,
                } as any);

                AppWideMetrics.registerConsentBannerInteractionEvent('manage-options');
            }

            if (preferenceAcceptBtn?.contains(event?.target)) {
                waitForConsentUpdate().then(() => {
                    firePrivacyPrefEvent({
                        acceptAll: true,
                        rejectAll: undefined,
                    });
                });

                AppWideMetrics.registerConsentBannerInteractionEvent(
                    'privacy-preferences-accept-all'
                );
            }

            if (preferenceRejectBtn?.contains(event?.target)) {
                waitForConsentUpdate().then(() => {
                    firePrivacyPrefEvent({
                        acceptAll: undefined,
                        rejectAll: true,
                    });
                });

                AppWideMetrics.registerConsentBannerInteractionEvent(
                    'privacy-preferences-reject-all'
                );
            }

            if (toggledNob) {
                const label = toggledNob.nextElementSibling;
                const input = label?.parentElement.previousElementSibling;
                const isToggledOn = !input.checked;

                if (label && label.classList.contains('ot-label-txt')) {
                    const labelValue = label.textContent.trim();
                    defaultPreferenceOptions[preferenceOptionsLookup[labelValue]] = isToggledOn;
                }
            }

            if (preferenceSaveBtn?.contains(event?.target)) {
                waitForConsentUpdate().then(() => {
                    const finalPreferences = initiatePreferenceOptions();
                    firePrivacyPrefEvent({
                        ...finalPreferences,
                        acceptAll: undefined,
                        rejectAll: undefined,
                    });
                });
            }
        }
    });
};

export default (window) => {
    trackingEventsOnOneTrust(window);
};
